"use client";

import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import OverlayNewsCard from "./OverlayNewsCard";

function OverlayCardListing({ items }) {
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);

  // Handle scroll events to update the current index with smoother tracking
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const handleScroll = () => {
      if (!scrollEl) return;

      // Get the scroll position and total scrollable width
      const scrollLeft = scrollEl.scrollLeft;
      const scrollWidth = scrollEl.scrollWidth;
      const clientWidth = scrollEl.clientWidth;
      const maxScroll = scrollWidth - clientWidth;

      // Calculate the scroll percentage (0 to 1)
      const scrollPercentage = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      setScrollPosition(scrollPercentage);

      // Calculate the current index more precisely
      const itemCount = items.length;
      const itemWidth = scrollWidth / itemCount;
      const rawIndex = scrollLeft / itemWidth;

      // Use the raw index for smoother transitions
      setCurrentIndex(Math.min(Math.max(0, rawIndex), itemCount - 1));
    };

    scrollEl.addEventListener("scroll", handleScroll);
    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, [items.length]);

  // Add touch/mouse drag functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.clientX;
    const walk = (startX - x) * 2; // Multiply for faster scrolling
    scrollRef.current.scrollLeft += walk;
    setStartX(x);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.touches[0].clientX;
    const walk = (startX - x) * 2;
    scrollRef.current.scrollLeft += walk;
    setStartX(x);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="relative w-full">
      <div
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
        ref={scrollRef}
        className={`flex overflow-x-auto pb-8 gap-4 snap-x no-scrollbar ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleDragEnd}
      >
        {items.map((item) => (
          <div key={item.id} className="snap-start">
            <OverlayNewsCard
              image={item.imageurl}
              title={item.subject}
              source={item.source}
              link={item.sourceurl}
            />
          </div>
        ))}
      </div>

      {/* Pagination dashes */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1.5 py-1">
        {items.map((_, index) => (
          <div
            key={index}
            className={`h-1 w-12 rounded-sm transition-all duration-300 ease-out ${
              index === Math.round(currentIndex)
                ? "bg-gray-400"
                : "bg-gray-300/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

OverlayCardListing.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      source: PropTypes.string.isRequired,
      link: PropTypes.string,
      category: PropTypes.string,
      date: PropTypes.string,
    })
  ).isRequired,
};

export default OverlayCardListing;
